/**
 * Wrapper fetch typé vers le BFF (`/api`).
 *
 * SITE_SPEC §1/§4 : origine unique, cookie de session httpOnly (SameSite=Lax),
 * jeton CSRF en double-submit pour toute mutation. Le navigateur ne parle QU'au BFF.
 *
 * - `credentials: "include"` sur chaque requête (envoie le cookie de session).
 * - Les mutations (POST/PUT/PATCH/DELETE) portent l'en-tête CSRF lu depuis un
 *   cookie non-httpOnly (`etheria_csrf`) — double-submit standard.
 * - Enveloppe d'erreur `{ ok:false, error, message }` (WEBAPI/BFF §2) → `ApiError`.
 */

import type { ApiError as ApiErrorDto } from "@etheria/shared";

/** Préfixe d'API. En dev, Vite proxy `/api` → BFF ; en prod le BFF sert le build. */
const API_BASE = "/api";

/** Nom du cookie CSRF (double-submit) et de l'en-tête associé. */
const CSRF_COOKIE = "etheria_csrf";
const CSRF_HEADER = "x-csrf-token";

/** Erreur d'API typée, portant le code HTTP et le code d'erreur métier. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** Lit un cookie côté navigateur (pour le jeton CSRF). */
function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"),
  );
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

type QueryValue = string | number | boolean | undefined | null;
export type QueryParams = Record<string, QueryValue>;

function buildUrl(path: string, params?: QueryParams): string {
  const url = path.startsWith("/api") ? path : `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
  if (!params) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

interface RequestOptions {
  params?: QueryParams;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const isMutation = method !== "GET" && method !== "HEAD";
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...opts.headers,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (isMutation) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.params), {
      method,
      credentials: "include",
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: opts.signal ?? null,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "network_error", "Impossible de joindre le serveur.");
  }

  // 204 / corps vide.
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const err = data as Partial<ApiErrorDto> | undefined;
    throw new ApiError(res.status, err?.error ?? "http_error", err?.message);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** Client d'API typé consommé par les hooks TanStack Query. */
export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("POST", path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PUT", path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PATCH", path, body, opts),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, undefined, opts),
};

/**
 * POST binaire brut (application/octet-stream) — upload de JAR de déploiement (DEPLOY_PIPELINE_SPEC).
 * Envoie `body` (Blob/ArrayBuffer) tel quel, avec le jeton CSRF (mutation) + cookie de session. Les
 * métadonnées (name/sha256) passent en query string. Renvoie le corps JSON parsé, mêmes erreurs
 * normalisées (`ApiError`) que `request`.
 */
export async function postRaw<T>(
  path: string,
  body: BodyInit,
  opts: { params?: QueryParams; signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/octet-stream",
  };
  const csrf = readCookie(CSRF_COOKIE);
  if (csrf) headers[CSRF_HEADER] = csrf;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.params), {
      method: "POST",
      credentials: "include",
      headers,
      body,
      signal: opts.signal ?? null,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "network_error", "Impossible de joindre le serveur.");
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? safeJson(text) : undefined;
  if (!res.ok) {
    const err = data as Partial<ApiErrorDto> | undefined;
    throw new ApiError(res.status, err?.error ?? "http_error", err?.message);
  }
  return data as T;
}

/** URL absolue vers un proxy BFF (carte, tuiles…) pour les `<img>`/embeds. */
export function apiUrl(path: string, params?: QueryParams): string {
  return buildUrl(path, params);
}
